type ResourceTypeOption = {
  type: string;
  subcategory: string;
};

export type ResourceTypeGroup = {
  key: string;
  label: string;
  types: string[];
};

export const groupResourceTypes = (options: ResourceTypeOption[]): ResourceTypeGroup[] => {
  const typesBySubcategory = new Map<string, string[]>();

  options.forEach(({ type, subcategory }) => {
    const existing = typesBySubcategory.get(subcategory);
    if (existing) {
      existing.push(type);
    } else {
      typesBySubcategory.set(subcategory, [type]);
    }
  });

  return Array.from(typesBySubcategory.entries())
    .map(([subcategory, types]) => ({
      key: subcategory,
      label: subcategory,
      types: [...types].sort(),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

// A group that matched a search: `types` stays the group's FULL, unfiltered
// list (selection state and the group checkbox's cascade must always act on
// the whole group, same as "All resources" does — not just what's visible
// during search), while `matchingTypes` is the narrower subset to actually
// render as rows.
export type FilteredResourceTypeGroup = ResourceTypeGroup & {
  matchingTypes: string[];
};

export const filterGroups = (
  groups: ResourceTypeGroup[],
  query: string
): FilteredResourceTypeGroup[] => {
  const normalizedQuery = query.trim().toLowerCase();

  if (!normalizedQuery) {
    return groups.map((group) => ({ ...group, matchingTypes: group.types }));
  }

  return groups.reduce<FilteredResourceTypeGroup[]>((acc, group) => {
    const labelMatches = group.label.toLowerCase().includes(normalizedQuery);
    const matchingTypes = labelMatches
      ? group.types
      : group.types.filter((type) => type.toLowerCase().includes(normalizedQuery));

    if (matchingTypes.length > 0) {
      acc.push({ ...group, matchingTypes });
    }

    return acc;
  }, []);
};
